import { ROTAS_FAVORITAVEIS, rotaDesligada, rotaVisivelPorAtividade, type AtividadeEmpresa, type Permissao, type RotaFavoritavel } from '@triade/contracts';
import {
  Briefcase,
  CalendarDays,
  ChartPie,
  Calculator,
  CalendarRange,
  ClipboardList,
  Coins,
  CreditCard,
  Database,
  FileText,
  Factory,
  FolderTree,
  ListChecks,
  Package,
  Percent,
  Receipt,
  RefreshCw,
  Ruler,
  Scale,
  Settings2,
  ShoppingCart,
  Store,
  SlidersHorizontal,
  Table2,
  Truck,
  UserRound,
  Users,
  UsersRound,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';

/** Menu lateral por trabalho (docs/design/Main.dc.html). Itens sem `rota` aparecem desabilitados ("Em breve"). */

/** Rotas que existem hoje. Ao criar uma tela, acrescente o path aqui e na uniao. */
export type RotaApp =
  | '/'
  | '/precificar/formacao'
  | '/precificar/lista'
  | '/precificar/simulador'
  | '/precificar/margens'
  | '/vender/orcamentos'
  | '/clientes'
  | '/plano'
  | '/plano/carteira'
  | '/plano/compras'
  | '/plano/necessidade'
  | '/plano/custo-fabrica'
  | '/plano/fluxo'
  | '/cadastros/itens'
  | '/cadastros/categorias'
  | '/cadastros/unidades'
  | '/cadastros/fornecedores'
  | '/cadastros/pessoas'
  | '/cadastros/cargos'
  | '/cadastros/imobilizados'
  | '/cadastros/condicoes'
  | '/cadastros/tabelas'
  | '/custos/fichas-tecnicas'
  | '/custos/centros'
  | '/custos/criterios'
  | '/custos/cifs'
  | '/custos/itens-cif'
  | '/custos/configuracoes-rateio'
  | '/custos/rateios'
  | '/custos/pessoal'
  | '/custos/atualizacoes'
  | '/configuracoes/empresa'
  | '/configuracoes/reforma'
  | '/configuracoes/plano'
  | '/configuracoes/integracoes'
  | '/configuracoes/erp'
  | '/configuracoes/onboarding'
  | '/configuracoes/usuarios'
  | '/configuracoes/empresas'
  | '/configuracoes/grupos'
  | '/configuracoes/tenants'
  | '/configuracoes/perfis';

export interface ItemNavegacao {
  titulo: string;
  rota?: RotaApp;
  /** Icone do item (lucide): mesmo icone na linha do grupo e na secao Favoritos. */
  icone?: LucideIcon;
  /** Permissao que mostra o item no menu (a API nega de qualquer jeito; a tela so esconde). Sem ela, todos veem. */
  permissao?: Permissao;
}

/** Subgrupo dentro de um grupo (ex.: Custos > Rateio). Sem itens permitidos, some. */
export interface SubgrupoNavegacao {
  titulo: string;
  icone?: LucideIcon;
  itens: ItemNavegacao[];
}

export interface GrupoNavegacao {
  titulo: string;
  /** Icone do grupo (lucide): cabecalho no menu expandido e botao do trilho recolhido. */
  icone: LucideIcon;
  itens: Array<ItemNavegacao | SubgrupoNavegacao>;
}

export function ehSubgrupo(entrada: ItemNavegacao | SubgrupoNavegacao): entrada is SubgrupoNavegacao {
  return 'itens' in entrada;
}

export const GRUPOS_NAVEGACAO: GrupoNavegacao[] = [
  {
    titulo: 'Precificar',
    icone: Calculator,
    itens: [
      { titulo: 'Formação de preço', icone: Calculator, rota: '/precificar/formacao', permissao: 'precificacao.simular' },
      { titulo: 'Lista de preços', icone: ListChecks, rota: '/precificar/lista', permissao: 'lista.ver' },
      { titulo: 'Simulador de cenários', icone: SlidersHorizontal, rota: '/precificar/simulador', permissao: 'precificacao.simular' },
      { titulo: 'Painel de margens', icone: ChartPie, rota: '/precificar/margens', permissao: 'margens.ver' },
    ],
  },
  {
    titulo: 'Vender',
    icone: ShoppingCart,
    itens: [
      { titulo: 'Orçamentos', icone: FileText, rota: '/vender/orcamentos', permissao: 'orcamento.ver' },
      { titulo: 'Clientes', icone: UserRound, rota: '/clientes', permissao: 'cliente.ver' },
    ],
  },
  {
    titulo: 'Plano',
    icone: CalendarRange,
    itens: [
      { titulo: 'Plano do mês', icone: CalendarRange, rota: '/plano', permissao: 'plano.ver' },
      { titulo: 'Carteira', icone: ClipboardList, rota: '/plano/carteira', permissao: 'carteira.ver' },
      { titulo: 'Necessidade de MP', icone: Package, rota: '/plano/necessidade', permissao: 'plano.ver' },
      { titulo: 'Compras de MP', icone: ShoppingCart, rota: '/plano/compras', permissao: 'plano.ver' },
      { titulo: 'Custo fábrica', icone: Factory, rota: '/plano/custo-fabrica', permissao: 'plano.ver' },
      { titulo: 'Fluxo de caixa', icone: Coins, rota: '/plano/fluxo', permissao: 'plano.ver' },
    ],
  },
  {
    titulo: 'Custos',
    icone: Coins,
    itens: [
      { titulo: 'Fichas técnicas', icone: ClipboardList, rota: '/custos/fichas-tecnicas', permissao: 'ficha.ver' },
      { titulo: 'Atualizações de custo', icone: RefreshCw, rota: '/custos/atualizacoes', permissao: 'lote.ver' },
      { titulo: 'Custo de pessoal', icone: UsersRound, rota: '/custos/pessoal', permissao: 'pessoal.ver' },
      {
        titulo: 'Rateio',
        icone: Percent,
        itens: [
          { titulo: 'Critérios de rateio', icone: Scale, rota: '/custos/criterios', permissao: 'centro.ver' },
          { titulo: 'Configurações de rateio', icone: Settings2, rota: '/custos/configuracoes-rateio', permissao: 'rateio.ver' },
          { titulo: 'Lançamentos de CIF', icone: Receipt, rota: '/custos/cifs', permissao: 'rateio.ver' },
          { titulo: 'Itens de CIF', icone: ListChecks, rota: '/custos/itens-cif', permissao: 'rateio.ver' },
          { titulo: 'Rateios mensais', icone: CalendarDays, rota: '/custos/rateios', permissao: 'rateio.ver' },
        ],
      },
    ],
  },
  {
    titulo: 'Cadastros',
    icone: Database,
    itens: [
      { titulo: 'Produtos e serviços', icone: Package, rota: '/cadastros/itens', permissao: 'item.ver' },
      { titulo: 'Categorias', icone: FolderTree, rota: '/cadastros/categorias', permissao: 'item.ver' },
      { titulo: 'Unidades', icone: Ruler, rota: '/cadastros/unidades', permissao: 'item.ver' },
      { titulo: 'Fornecedores', icone: Truck, rota: '/cadastros/fornecedores', permissao: 'custo.ver' },
      { titulo: 'Imobilizados', icone: Warehouse, rota: '/cadastros/imobilizados', permissao: 'centro.ver' },
      { titulo: 'Centros de custo', icone: Factory, rota: '/custos/centros', permissao: 'centro.ver' },
      {
        titulo: 'Pessoas',
        icone: UsersRound,
        itens: [
          { titulo: 'Pessoas', icone: Users, rota: '/cadastros/pessoas', permissao: 'rh.ver' },
          { titulo: 'Cargos', icone: Briefcase, rota: '/cadastros/cargos', permissao: 'rh.ver' },
        ],
      },
      {
        titulo: 'Comercial',
        icone: Store,
        itens: [
          { titulo: 'Tabelas de preço', icone: Table2, rota: '/cadastros/tabelas', permissao: 'tabela.ver' },
          { titulo: 'Condições de pagamento', icone: CreditCard, rota: '/cadastros/condicoes', permissao: 'tabela.ver' },
        ],
      },
    ],
  },
];

/** Menu do ⚙ (rodape/cabecalho, outra tarefa): Empresa e Usuarios e acessos saem do corpo do menu e ficam aqui. */
export const CONFIGURACOES_NAVEGACAO: ReadonlyArray<ItemNavegacao & { rota: RotaApp; permissao: Permissao }> = [
  { titulo: 'Empresa', rota: '/configuracoes/empresa', permissao: 'empresa.ver' },
  { titulo: 'Reforma tributária', rota: '/configuracoes/reforma', permissao: 'parametros.ver' },
  { titulo: 'Plano de vendas', rota: '/configuracoes/plano', permissao: 'plano.ver' },
  { titulo: 'Integrações', rota: '/configuracoes/integracoes', permissao: 'integracoes.gerenciar' },
  { titulo: 'Onboarding', rota: '/configuracoes/onboarding', permissao: 'onboarding.executar' },
  { titulo: 'Usuários e acessos', rota: '/configuracoes/usuarios', permissao: 'usuarios.gerenciar' },
  { titulo: 'Perfis de acesso', rota: '/configuracoes/perfis', permissao: 'perfis.gerenciar' },
  { titulo: 'Empresas', rota: '/configuracoes/empresas', permissao: 'empresas.gerenciar' },
  { titulo: 'Grupos', rota: '/configuracoes/grupos', permissao: 'empresas.gerenciar' },
  { titulo: 'Tenants', rota: '/configuracoes/tenants', permissao: 'tenants.gerenciar' },
];

/** Itens de um grupo (ou da lista de entradas) com os subgrupos abertos: lista plana, na ordem do menu. */
export function achatarItens(entradas: ReadonlyArray<ItemNavegacao | SubgrupoNavegacao>): ItemNavegacao[] {
  return entradas.flatMap((e) => (ehSubgrupo(e) ? e.itens : [e]));
}

/** Rotas de todos os itens do menu (sem Configuracoes), na ordem da tela. */
export const ROTAS_DO_MENU: readonly RotaApp[] = GRUPOS_NAVEGACAO.flatMap((g) => achatarItens(g.itens))
  .map((i) => i.rota)
  .filter((r): r is RotaApp => r !== undefined);

/** Local do grupo (e do subgrupo, se a rota estiver dentro de um) que contem a rota; undefined se a rota nao esta em grupo (inicio). */
export interface LocalNavegacao {
  grupo: string;
  subgrupo?: string;
}

function casaRota(caminho: string, rota: string | undefined): boolean {
  return rota !== undefined && (caminho === rota || caminho.startsWith(`${rota}/`));
}

export function grupoDaRota(caminho: string, grupos: readonly GrupoNavegacao[] = GRUPOS_NAVEGACAO): LocalNavegacao | undefined {
  for (const grupo of grupos) {
    for (const entrada of grupo.itens) {
      if (ehSubgrupo(entrada)) {
        if (entrada.itens.some((i) => casaRota(caminho, i.rota))) return { grupo: grupo.titulo, subgrupo: entrada.titulo };
      } else if (casaRota(caminho, entrada.rota)) {
        return { grupo: grupo.titulo };
      }
    }
  }
  return undefined;
}

/**
 * Menu so com o que o usuario pode usar. Item sem permissao some; subgrupo sem item permitido some; grupo sem entrada some.
 * `pode` vem de GET /me (enquanto carrega, nada e permitido). `atividades` (perfil da empresa, so exibicao): item cuja rota
 * nao cabe na atividade some (ex.: Fichas tecnicas numa empresa so COMERCIO). Sem atividades informadas, nada e escondido.
 * `desligados` (Sessao 37, Configuracoes > Empresa > Menus): item desligado pela empresa some, por cima da atividade.
 */
export function filtrarGrupos(
  grupos: readonly GrupoNavegacao[],
  pode: (permissao: Permissao) => boolean,
  atividades?: readonly AtividadeEmpresa[],
  desligados?: readonly string[],
): GrupoNavegacao[] {
  const permitido = (i: ItemNavegacao) =>
    (!i.permissao || pode(i.permissao)) && (!i.rota || (rotaVisivelPorAtividade(i.rota, atividades) && !rotaDesligada(i.rota, desligados)));
  return grupos
    .map((g) => ({
      ...g,
      itens: g.itens.flatMap((entrada): Array<ItemNavegacao | SubgrupoNavegacao> => {
        if (ehSubgrupo(entrada)) {
          const itens = entrada.itens.filter(permitido);
          return itens.length > 0 ? [{ ...entrada, itens }] : [];
        }
        return permitido(entrada) ? [entrada] : [];
      }),
    }))
    .filter((g) => g.itens.length > 0);
}

/** Itens de Configuracoes que o usuario pode abrir (aba permitida). */
export function configuracoesPermitidas(pode: (permissao: Permissao) => boolean): ItemNavegacao[] {
  return CONFIGURACOES_NAVEGACAO.filter((i) => pode(i.permissao));
}

/** Titulo (h1) da tela de Configuracoes: o nome da aba aberta; no indice (/configuracoes), 'Configurações'. */
export function tituloConfiguracoes(caminho: string): string {
  return CONFIGURACOES_NAVEGACAO.find((a) => casaRota(caminho, a.rota))?.titulo ?? 'Configurações';
}

/**
 * Permissao que cada tela exige (prefixo do caminho; vale o mais longo). Lista: qualquer uma delas basta (/configuracoes).
 * A API e quem nega; sem a permissao o AppShell mostra "sem acesso" em vez da tela. Rotas fora da tabela (inicio) sao de todos.
 */
export type ExigenciaRota = Permissao | readonly Permissao[];

const PERMISSAO_POR_ROTA: ReadonlyArray<readonly [string, ExigenciaRota]> = [
  ['/precificar/formacao', 'precificacao.simular'],
  ['/precificar/simulador', 'precificacao.simular'],
  ['/precificar/lista', 'lista.ver'],
  ['/precificar/margens', 'margens.ver'],
  ['/vender/orcamentos/novo', 'orcamento.editar'],
  ['/vender/orcamentos', 'orcamento.ver'],
  ['/clientes', 'cliente.ver'],
  ['/plano', 'plano.ver'],
  ['/plano/carteira', 'carteira.ver'],
  ['/plano/compras', 'plano.ver'],
  ['/plano/necessidade', 'plano.ver'],
  ['/plano/custo-fabrica', 'plano.ver'],
  ['/plano/fluxo', 'plano.ver'],
  ['/cadastros/itens/novo', 'item.editar'],
  ['/cadastros/itens', 'item.ver'],
  ['/cadastros/categorias', 'item.ver'],
  ['/cadastros/unidades', 'item.ver'],
  ['/cadastros/fornecedores', 'custo.ver'],
  ['/cadastros/pessoas', 'rh.ver'],
  ['/cadastros/cargos', 'rh.ver'],
  ['/cadastros/imobilizados', 'centro.ver'],
  ['/cadastros/condicoes', 'tabela.ver'],
  ['/cadastros/tabelas', 'tabela.ver'],
  ['/custos/fichas-tecnicas', 'ficha.ver'],
  ['/custos/centros', 'centro.ver'],
  ['/custos/criterios', 'centro.ver'],
  ['/custos/cifs', 'rateio.ver'],
  ['/custos/itens-cif', 'rateio.ver'],
  ['/custos/configuracoes-rateio', 'rateio.ver'],
  ['/custos/rateios', 'rateio.ver'],
  ['/custos/pessoal', 'pessoal.ver'],
  ['/custos/atualizacoes', 'lote.ver'],
  ['/configuracoes/empresa', 'empresa.ver'],
  ['/configuracoes/reforma', 'parametros.ver'],
  ['/configuracoes/plano', 'plano.ver'],
  ['/configuracoes/integracoes', 'integracoes.gerenciar'],
  ['/configuracoes/erp', 'integracoes.gerenciar'],
  ['/configuracoes/onboarding', 'onboarding.executar'],
  ['/configuracoes/usuarios', 'usuarios.gerenciar'],
  ['/configuracoes/empresas', 'empresas.gerenciar'],
  ['/configuracoes/grupos', 'empresas.gerenciar'],
  ['/configuracoes/tenants', 'tenants.gerenciar'],
  ['/configuracoes/perfis', 'perfis.gerenciar'],
  ['/configuracoes', ['empresa.ver', 'parametros.ver', 'usuarios.gerenciar', 'perfis.gerenciar', 'empresas.gerenciar', 'integracoes.gerenciar']],
];

export function permissaoDaRota(caminho: string): ExigenciaRota | null {
  const achados = PERMISSAO_POR_ROTA.filter(([prefixo]) => caminho === prefixo || caminho.startsWith(`${prefixo}/`));
  achados.sort((a, b) => b[0].length - a[0].length);
  return achados[0]?.[1] ?? null;
}

/** Verdadeiro se a exigencia (uma permissao, ou qualquer uma da lista) esta concedida. */
export function temAcesso(exigencia: ExigenciaRota, pode: (permissao: Permissao) => boolean): boolean {
  return typeof exigencia === 'string' ? pode(exigencia) : exigencia.some((p) => pode(p));
}

/** Item do menu (grupo ou subgrupo) de uma rota, se ela esta no menu. Configuracoes nao entra (nao fixa). */
export function itemDoMenu(rota: string): ItemNavegacao | undefined {
  for (const grupo of GRUPOS_NAVEGACAO) {
    for (const item of achatarItens(grupo.itens)) {
      if (item.rota === rota) return item;
    }
  }
  return undefined;
}

/**
 * Itens da secao Favoritos: na ordem em que foram fixados (ordem da API), so os que estao no menu e que o usuario pode ver
 * (mesma regra de filtrarGrupos, inclusive o perfil de atividade). Favorito sem permissao, oculto pela atividade ou fora do menu nao aparece.
 */
export function itensFavoritos(
  rotas: readonly string[],
  pode: (permissao: Permissao) => boolean,
  atividades?: readonly AtividadeEmpresa[],
  desligados?: readonly string[],
): ItemNavegacao[] {
  return rotas.flatMap((rota) => {
    const item = itemDoMenu(rota);
    if (!item || !item.rota) return [];
    if (!rotaVisivelPorAtividade(item.rota, atividades) || rotaDesligada(item.rota, desligados)) return [];
    return !item.permissao || pode(item.permissao) ? [item] : [];
  });
}

/** Verdadeiro se a rota pode ser fixada nos favoritos (lista ROTAS_FAVORITAVEIS da API). */
export function ehRotaFavoritavel(rota: string): rota is RotaFavoritavel {
  return (ROTAS_FAVORITAVEIS as readonly string[]).includes(rota);
}
